import { getMyPermissions, type PermissionItem } from "@/api/teacher"

// 定义路由元信息接口
interface RouteMeta {
  title: string
  icon?: string
  showLink: boolean
  backstage: boolean
  rank: number
  roles: string[]
  auths?: string[] // 页面级别的按钮权限
  perms?: string
  noCache?: boolean
  keepAlive?: boolean
  hiddenTag?: boolean // 是否在标签页中隐藏（父菜单不显示为标签）
}

// 定义动态路由接口
interface DynamicRoute {
  path: string
  name: string
  component?: () => Promise<any>
  redirect?: string
  meta: RouteMeta
  children?: DynamicRoute[]
}

export const getAsyncRoutes = (): Promise<any[]> => {
  return new Promise(async (resolve) => {
    try {
      // 获取当前选中的课程ID
      const { useCourseStore } = await import("@/store/modules/course")
      const courseStore = useCourseStore()
      const currentCourseId = courseStore.selectedCourseId
      console.log("当前选中的课程ID:", currentCourseId)

      // 获取用户权限数据
      const permissions = await getMyPermissions(currentCourseId || undefined)
      console.log("获取到的权限数据:", permissions)

      // HTTP拦截器已经处理了响应格式，直接使用返回的数据
      const permissionList = Array.isArray(permissions) ? permissions : []

      if (!permissionList || permissionList.length === 0) {
        console.warn("用户没有任何权限，动态路由为空")
        const { usePermissionStoreHook } = await import("@/store/modules/permission")
        usePermissionStoreHook().handleWholeMenus([])
        resolve([])
        return
      }

      // 存储权限数据到store
      const { usePermissionStoreHook } = await import("@/store/modules/permission")
      usePermissionStoreHook().setPermissions(permissionList)

      // 转换权限数据为路由和菜单（包含层级结构）
      const routes = transformPermissionsToRoutes(permissionList)
      console.log("生成的动态路由:", routes)

      // ✅ 关键修复：注册路由到Vue Router
      await registerRoutesToRouter(routes)
      console.log("初始化时动态路由已注册到Vue Router")

      // 立即设置层级菜单到store中
      usePermissionStoreHook().handleWholeMenus(routes, true)

      // 验证store中的数据
      setTimeout(() => {
        const currentMenus = usePermissionStoreHook().wholeMenus
        console.log("验证store中的菜单数据:", currentMenus)
        console.log("菜单数据长度:", currentMenus.length)
        if (currentMenus.length === 0) {
          console.error("菜单数据设置失败！store中菜单为空")
        }
      }, 1000)

      resolve(routes)
    } catch (error) {
      console.error("获取权限数据失败:", error)

      // 显示错误信息给用户
      const errorMessage = error?.response?.data?.message || error.message || "获取权限数据失败"
      console.error("权限错误详情:", errorMessage)

      // 使用Element Plus显示错误提示
      try {
        const { ElMessage } = await import("element-plus")
        ElMessage.error(`权限获取失败: ${errorMessage}`)
      } catch (msgError) {
        console.warn("无法显示错误提示:", msgError)
      }

      try {
        const { usePermissionStoreHook } = await import("@/store/modules/permission")
        usePermissionStoreHook().handleWholeMenus([], true)
      } catch (storeError) {
        console.error("清空菜单时出错:", storeError)
      }

      resolve([])
    }
  })
}

// 将权限数据转换为前端路由（生成层级菜单结构）
function transformPermissionsToRoutes(permissions: PermissionItem[]): DynamicRoute[] {
  console.log("开始转换权限数据为路由, 权限数量:", permissions.length)

  // 过滤权限数据：只保留教师端的菜单和页面
  const filteredPermissions = permissions.filter((permission) => {
    // 过滤掉学生端权限（perms以student:开头的）
    if (permission.perms && permission.perms.startsWith("student:")) {
      return false
    }

    // 过滤掉已在静态路由中存在的权限
    // 注意：工作台模块(workbench)权限应该保留，因为它们是动态权限
    if (permission.perms === "admin:dashboard" || permission.routePath === "/dashboard") {
      return false
    }

    // 隐藏指定的菜单项 TODO important
    const hiddenMenus = ["命题计划", "题目管理"]
    if (hiddenMenus.includes(permission.menuName)) {
      console.log("隐藏菜单项:", permission.menuName)
      return false
    }

    // 只保留菜单(M)和页面(C)，功能(F)用于按钮权限控制
    return permission.menuType === "M" || permission.menuType === "C"
  })

  console.log("过滤后的权限数量:", filteredPermissions.length)

  // 构建权限映射表
  const permissionMap = new Map<number, PermissionItem>()
  filteredPermissions.forEach((permission) => {
    permissionMap.set(permission.id, permission)
  })

  // 提取按钮权限映射（F类型的权限）
  const buttonPermissions = extractButtonPermissions(permissions)

  // 构建层级菜单：找到根权限（pid为0），然后递归构建子菜单
  const rootPermissions = filteredPermissions.filter((permission) => permission.pid === 0 && permission.hidden !== 1)

  console.log("根权限数量:", rootPermissions.length)

  const routes: DynamicRoute[] = []

  // 构建菜单层级（包括隐藏的子页面）
  rootPermissions.forEach((rootPermission) => {
    const route = buildMenuHierarchy(rootPermission, permissionMap, buttonPermissions)
    if (route) {
      routes.push(route)
    }
  })

  // 处理“孤儿页面”：当后端仅返回目录(M)且其子页面(C)缺失pid关联时，尝试按 parentName 或路径前缀归并
  // const candidatePages = filteredPermissions.filter((p) => p.menuType === "C")
  // if (candidatePages.length) {
  //   candidatePages.forEach((pagePerm) => {
  //     // 已经在层级中（存在于某个父的children）则跳过
  //     const existsInTree = routes.some((r) => (r.children || []).some((c) => c.name === (pagePerm.routeName || pagePerm.menuName)))
  //     if (existsInTree) return

  //     const pageRoute = createPageRoute(pagePerm, buttonPermissions)
  //     if (!pageRoute) return

  //     let attached = false

  //     // 1) 优先通过 parentName 归并
  //     if (pagePerm.parentName) {
  //       const parentByName = routes.find((r) => r.name === pagePerm.parentName)
  //       if (parentByName) {
  //         parentByName.children = parentByName.children || []
  //         if (!parentByName.children.some((c) => c.path === pageRoute.path || c.name === pageRoute.name)) {
  //           parentByName.children.push(pageRoute)
  //           attached = true
  //         }
  //       }
  //     }

  //     // 2) 次之按路径前缀归并（如 /workbench/... -> 父级 /workbench）
  //     if (!attached && pageRoute.path) {
  //       const seg = pageRoute.path.split("/").filter(Boolean)[0]
  //       if (seg) {
  //         const parentByPath = routes.find((r) => r.path === `/${seg}`)
  //         if (parentByPath) {
  //           parentByPath.children = parentByPath.children || []
  //           if (!parentByPath.children.some((c) => c.path === pageRoute.path || c.name === pageRoute.name)) {
  //             parentByPath.children.push(pageRoute)
  //             attached = true
  //           }
  //         }
  //       }
  //     }

  //     // 3) 仍无法归并则作为顶级页面加入，避免菜单完全为空
  //     if (!attached && !routes.some((r) => r.path === pageRoute.path || r.name === pageRoute.name)) {
  //       routes.push(pageRoute)
  //     }
  //   })
  // }

  // 按sort排序
  routes.sort((a, b) => (a.meta.rank || 99) - (b.meta.rank || 99))

  console.log("生成的层级菜单数量:", routes.length)
  return routes
}

// 递归构建菜单层级结构
function buildMenuHierarchy(
  permission: PermissionItem,
  permissionMap: Map<number, PermissionItem>,
  buttonPermissions: Map<number, string[]>
): DynamicRoute | null {
  console.log("构建菜单层级:", permission.menuName, "类型:", permission.menuType, "hidden:", permission.hidden)

  // 查找子权限（包括隐藏的子页面）
  const children: DynamicRoute[] = []
  permissionMap.forEach((childPermission) => {
    // 包含所有子权限，无论是否隐藏
    // hidden=1 的页面仍然需要注册为路由，只是不在菜单显示
    if (childPermission.pid === permission.id) {
      const childRoute = buildMenuHierarchy(childPermission, permissionMap, buttonPermissions)
      if (childRoute) {
        children.push(childRoute)
      }
    }
  })

  // 按sort排序
  children.sort((a, b) => (a.meta.rank || 99) - (b.meta.rank || 99))

  // 生成路径
  let routePath: string
  if (permission.routePath) {
    routePath = permission.routePath
  } else if (permission.perms) {
    routePath = generatePathFromPerms(permission.perms)
  } else {
    routePath = `/${permission.menuName.toLowerCase().replace(/\s+/g, "-")}`
  }

  // 创建路由对象
  const route: DynamicRoute = {
    path: routePath,
    name: permission.routeName || permission.menuName || `Route${permission.id}`,
    meta: {
      title: permission.menuName,
      icon: permission.icon || "ep:menu",
      showLink: permission.hidden !== 1, // hidden=1的页面不在菜单显示
      backstage: true,
      rank: permission.sort || 99,
      roles: ["teacher"],
      perms: permission.perms,
      noCache: permission.noCache === 1,
      keepAlive: permission.noCache !== 1,
      // 🎯 父菜单（目录）不显示为标签页，因为它们没有组件只是容器
      hiddenTag: permission.menuType === "M"
    }
  }

  // 根据权限类型设置组件和其他属性
  if (permission.menuType === "M") {
    // 菜单类型：作为目录，保持层级结构
    if (children.length > 0) {
      route.children = children
      // 目录没有具体组件，但保持菜单层级结构
      route.component = undefined

      // 🎯 自动添加 redirect：父菜单默认重定向到第一个子页面
      const firstChild = children.find(child => child.meta?.showLink !== false && child.path)
      if (firstChild) {
        // 如果子路由路径是相对路径，需要拼接父路径
        const childPath = firstChild.path.startsWith('/')
          ? firstChild.path
          : `${routePath}/${firstChild.path}`
        route.redirect = childPath
        console.log(`自动为父菜单 ${permission.menuName} 添加 redirect:`, childPath)
      }
    } else {
      // 没有子菜单的目录，可能是占位符，跳过
      console.warn(`目录权限 ${permission.menuName} 没有子权限`)
      return null
    }
  } else if (permission.menuType === "C") {
    // 页面类型：有具体组件
    route.component = getPageComponent(permission)

    // 获取页面级别的按钮权限
    const auths = buttonPermissions.get(permission.id) || []
    if (auths.length > 0) {
      route.meta.auths = auths
    }

    // 如果有子菜单，也添加上
    if (children.length > 0) {
      route.children = children
    }
  }

  return route
}

// 提取按钮权限映射
function extractButtonPermissions(permissions: PermissionItem[]): Map<number, string[]> {
  const buttonPermissions = new Map<number, string[]>()

  permissions
    .filter((permission) => permission.menuType === "F") // 功能类型权限
    .forEach((permission) => {
      const pid = permission.pid
      if (!buttonPermissions.has(pid)) {
        buttonPermissions.set(pid, [])
      }
      if (permission.perms) {
        buttonPermissions.get(pid)!.push(permission.perms)
      }
    })

  return buttonPermissions
}

// 创建页面路由（扁平化结构）
function createPageRoute(permission: PermissionItem, buttonPermissions: Map<number, string[]>): DynamicRoute | null {
  console.log("创建页面路由:", permission.menuName, "路径:", permission.routePath || permission.component)

  // 获取组件
  const component = getPageComponent(permission)

  // 获取页面级别的按钮权限
  const auths = buttonPermissions.get(permission.id) || []

  // 创建扁平化的路由对象
  const route: DynamicRoute = {
    path: permission.routePath || generatePathFromPerms(permission.perms),
    name: permission.routeName || permission.menuName,
    component,
    meta: {
      title: permission.menuName,
      icon: permission.icon || "ep:menu",
      showLink: true,
      backstage: true,
      rank: permission.sort || 99,
      roles: ["teacher"],
      perms: permission.perms,
      noCache: permission.noCache === 1,
      keepAlive: permission.noCache !== 1,
      auths: auths.length > 0 ? auths : undefined
    }
  }

  return route
}

// 获取页面组件（只处理页面类型）
function getPageComponent(permission: PermissionItem): () => Promise<any> {
  // 页面类型，根据component字段获取具体组件
  if (permission.component && permission.component.trim()) {
    const componentPath = permission.component.trim()
    console.log(`动态导入组件: ${componentPath}`)

    // 使用映射表来处理动态导入，避免Vite的动态导入限制
    return () => getComponentByPath(componentPath, permission.menuName)
  }

  // 默认组件
  console.warn(`权限 ${permission.menuName} 没有指定组件，使用默认组件`)
  return () => import("@/views/user-admin/index.vue")
}

// 通过路径获取组件（避免Vite动态导入限制）
async function getComponentByPath(componentPath: string, menuName: string): Promise<any> {
  try {
    // 预定义的组件映射表（只包含实际存在的组件）
    const componentMap: Record<string, () => Promise<any>> = {
      // 试卷管理
      "user-admin/paper/browse/index": () => import("@/views/user-admin/paper/browse/index.vue"),
      "user-admin/paper/random/index": () => import("@/views/user-admin/paper/random/index.vue"),
      "user-admin/paper/basket/index": () => import("@/views/user-admin/paper/basket/index.vue"),
      "user-admin/paper/center/index": () => import("@/views/user-admin/paper/center/index.vue"),

      // 系统管理
      "user-admin/system/menu/index": () => import("@/views/user-admin/system/menu/index.vue"),
      "user-admin/system/role/index": () => import("@/views/user-admin/system/role/index.vue"),

      "user-admin/system/user/dept-user": () => import("@/views/user-admin/system/user/dept-user.vue"),
      "user-admin/system/course-category/index": () => import("@/views/user-admin/system/course-category/index.vue"),
      "user-admin/system/class-student/index": () => import("@/views/user-admin/system/class-student/index.vue"),
      "user-admin/system/project-cfg/index": () => import("@/views/user-admin/system/project-cfg/index.vue"),

      // 考试测评
      "user-admin/exam/list/index": () => import("@/views/user-admin/exam/list/index.vue"),
      "user-admin/exam/analysis/index": () => import("@/views/user-admin/exam/analysis/index.vue"),
      "user-admin/exam/correct/index": () => import("@/views/user-admin/exam/correct/index.vue"),
      "user-admin/exam/score/index": () => import("@/views/user-admin/exam/score/index.vue"),
      "user-admin/exam/score/children/score-detail/index": () => import("@/views/user-admin/exam/score/children/score-detail/index.vue"),

      // 命题计划
      "user-admin/propose/plan/index": () => import("@/views/user-admin/propose/plan/index.vue"),
      "user-admin/propose/mine/index": () => import("@/views/user-admin/propose/mine/index.vue"),
      "user-admin/propose/approval/index": () => import("@/views/user-admin/propose/approval/index.vue"),

      // 命题管理
      "user-admin/proposition/config/index": () => import("@/views/user-admin/proposition/config/index.vue"),
      "user-admin/proposition/monitor/index": () => import("@/views/user-admin/proposition/monitor/index.vue"),

      // 统计信息
      "user-admin/statistic/course/index": () => import("@/views/user-admin/statistic/course/index.vue"),
      "user-admin/statistic/quantity/index": () => import("@/views/user-admin/statistic/quantity/index.vue"),
      "user-admin/statistic/structure/index": () => import("@/views/user-admin/statistic/structure/index.vue"),
      "user-admin/statistic/ranking/index": () => import("@/views/user-admin/statistic/ranking/index.vue"),
      "user-admin/statistic/paper/index": () => import("@/views/user-admin/statistic/paper/index.vue"),
      "user-admin/statistic/alert/index": () => import("@/views/user-admin/statistic/alert/index.vue"),

      // 工作台模块
      "user-admin/workbench/dashboard/index": () => import("@/views/user-admin/workbench/dashboard/index.vue"),
      "user-admin/workbench/question/index": () => import("@/views/user-admin/workbench/question/index.vue"),
      "user-admin/workbench/audit/index": () => import("@/views/user-admin/workbench/audit/index.vue"),
      // "user-admin/workbench/question/edit": () => import("@/views/user-admin/workbench/question/edit.vue"),

      // 课程属性管理
      "user-admin/course-attribute/knowledge/index": () =>
        import("@/views/user-admin/course-attribute/knowledge/index.vue"),
      "user-admin/course-attribute/types/index": () => import("@/views/user-admin/course-attribute/types/index.vue"),
      "user-admin/course-attribute/rank/index": () => import("@/views/user-admin/course-attribute/rank/index.vue"),
      "user-admin/course-attribute/diff/index": () => import("@/views/user-admin/course-attribute/diff/index.vue"),
      "user-admin/course-attribute/other-config/index": () =>
        import("@/views/user-admin/course-attribute/other-config/index.vue"),
      "user-admin/course-attribute/scheme-types/index": () =>
        import("@/views/user-admin/course-attribute/scheme-types/index.vue"),

      // 系统管理 - 操作日志
      "user-admin/system/log/index": () => import("@/views/user-admin/system/log/index.vue")
    }

    const importFn = componentMap[componentPath]
    if (importFn) {
      console.log(`从映射表导入组件: ${componentPath}`)
      return await importFn()
    } else {
      console.warn(`组件映射表中未找到: ${componentPath}`)
      throw new Error(`Component not found in mapping: ${componentPath}`)
    }
  } catch (error) {
    console.warn(`组件导入失败: ${componentPath}`, error)
    console.log(`使用占位组件代替: ${componentPath}`)

    // 组件不存在时，返回一个占位组件
    return createPlaceholderComponent(menuName, componentPath)
  }
}

// 根据权限标识生成路径
function generatePathFromPerms(perms?: string): string {
  if (!perms) return "/unknown"

  // 将权限标识转换为路径，例如 "admin:system:menu" -> "/admin/system/menu"
  return "/" + perms.replace(/:/g, "/")
}

// 创建占位组件（当组件文件不存在时使用）
function createPlaceholderComponent(menuName: string, componentPath: string) {
  return {
    default: {
      name: "PlaceholderComponent",
      template: `
        <div style="display: flex; flex-direction: column; align-items: center; justify-content: center; height: 400px; padding: 20px; text-align: center;">
          <div style="font-size: 48px; color: #faad14; margin-bottom: 16px;">⚠️</div>
          <h2 style="color: #666; margin-bottom: 8px;">组件待开发</h2>
          <p style="color: #999; margin-bottom: 24px; max-width: 500px;">
            页面"${menuName}"对应的组件文件 @/views/${componentPath}.vue 不存在，请联系开发人员添加。
          </p>
          <button 
            style="padding: 8px 16px; background-color: #1890ff; color: white; border: none; border-radius: 4px; cursor: pointer;"
            @click="goBack"
          >
            返回上级
          </button>
        </div>
      `,
      methods: {
        goBack() {
          this.$router.back()
        }
      }
    }
  }
}

// 刷新权限菜单（当课程选择发生变化时调用）
export const refreshPermissionMenus = async (course_id?: number): Promise<void> => {
  console.log("=== 开始刷新权限菜单 ===")
  console.log("课程ID:", course_id)

  try {
    // 重置菜单状态
    const { usePermissionStoreHook } = await import("@/store/modules/permission")
    const permissionStore = usePermissionStoreHook()
    permissionStore.clearAllCachePage() // 清空当前菜单

    // 获取新的权限数据
    const permissions = await getMyPermissions(course_id)
    console.log("刷新获取到的权限数据:", permissions)

    // HTTP拦截器已经处理了响应格式，直接使用返回的数据
    const permissionList = Array.isArray(permissions) ? permissions : []
    console.log("处理后的权限列表长度:", permissionList.length)

    if (!permissionList || permissionList.length === 0) {
      console.warn("刷新时用户没有任何权限，显示基础路由")
      // 显示提示信息
      try {
        const { ElMessage } = await import("element-plus")
        ElMessage.warning("当前课程没有可用的菜单权限")
      } catch (msgError) {
        console.warn("无法显示警告提示:", msgError)
      }

      // 提供基础的工作台路由
      const basicRoutes = [
        {
          path: "/dashboard",
          name: "Dashboard",
          component: () => import("@/views/user-admin/workbench/dashboard/index.vue"),
          meta: {
            title: "工作台",
            icon: "ep:home-filled",
            showLink: true,
            backstage: true,
            rank: 0,
            roles: ["teacher"]
          }
        }
      ]

      permissionStore.handleWholeMenus(basicRoutes, true)
      console.log("刷新时基础路由设置完成")
      return
    }

    // 存储权限数据到store
    permissionStore.setPermissions(permissionList)
    console.log("刷新时权限数据已存储到store")

    // 转换权限数据为路由和菜单
    const routes = transformPermissionsToRoutes(permissionList)
    console.log("刷新时生成的动态路由数量:", routes.length)

    // ✅ 关键修复：注册路由到Vue Router
    await registerRoutesToRouter(routes)
    console.log("刷新时动态路由已注册到Vue Router")

    // 设置新的菜单到store中
    permissionStore.handleWholeMenus(routes, true)
    console.log("刷新时动态路由已设置到store")

    // 验证菜单设置结果
    setTimeout(async () => {
      const currentMenus = permissionStore.wholeMenus
      console.log("刷新后验证store中的菜单数据:", currentMenus)
      console.log("刷新后菜单数据长度:", currentMenus.length)

      if (currentMenus.length === 0) {
        console.error("刷新后菜单数据设置失败！store中菜单为空")
        // 显示错误提示
        try {
          const { ElMessage } = await import("element-plus")
          ElMessage.error("菜单权限更新失败：数据为空")
        } catch (msgError) {
          console.warn("无法显示错误提示:", msgError)
        }
      } else {
        // 显示成功提示
        try {
          const { ElMessage } = await import("element-plus")
          //   ElMessage.success("菜单权限已更新")
        } catch (msgError) {
          console.warn("无法显示成功提示:", msgError)
        }
      }
    }, 2000)
  } catch (error) {
    console.error("刷新权限菜单失败:", error)

    // 显示错误信息给用户
    const errorMessage = error?.response?.data?.message || error.message || "刷新权限菜单失败"
    console.error("刷新权限错误详情:", errorMessage)

    // 使用Element Plus显示错误提示
    try {
      const { ElMessage } = await import("element-plus")
      ElMessage.error(`菜单权限更新失败: ${errorMessage}`)
    } catch (msgError) {
      console.warn("无法显示错误提示:", msgError)
    }

    // 错误时保持当前菜单状态，不做清空操作
    throw error
  }
}

// ✅ 新增：注册路由到Vue Router的函数
async function registerRoutesToRouter(routes: DynamicRoute[]): Promise<void> {
  try {
    // 动态导入router实例
    const { default: router } = await import("@/router")
    const { formatFlatteningRoutes } = await import("@/router/utils")

    // 扁平化路由处理
    const flattenedRoutes = formatFlatteningRoutes(routes as any)
    console.log("扁平化后的路由数量:", flattenedRoutes.length)

    // 清除已存在的动态路由，避免重复注册
    const currentRoutes = router.options.routes[0]?.children || []
    const existingDynamicRoutes = currentRoutes.filter((route: any) => route.meta?.backstage)

    console.log("清除已存在的动态路由数量:", existingDynamicRoutes.length)
    existingDynamicRoutes.forEach((route: any) => {
      const index = currentRoutes.findIndex((r: any) => r.path === route.path)
      if (index !== -1) {
        currentRoutes.splice(index, 1)
      }
      // 同时从Vue Router中移除
      if (router.hasRoute(route.name)) {
        router.removeRoute(route.name)
        console.log("移除已存在的路由:", route.name)
      }
    })

    // 注册新的动态路由
    flattenedRoutes.forEach((v: any) => {
      // 确保路由配置正确
      if (!v.path || !v.name) {
        console.warn("跳过无效路由:", v)
        return
      }

      try {
        // 添加到主布局的children中
        if (router.options.routes[0]?.children) {
          router.options.routes[0].children.push(v)
        }

        // 将动态路由添加为主布局路由的子路由
        router.addRoute("Home", v) // "Home" 是主布局路由的name
        console.log("成功注册路由:", v.name, "路径:", v.path)
      } catch (error) {
        console.error("注册路由失败:", v.path, error)
      }
    })

    // 设置扁平化路由到store
    const { usePermissionStoreHook } = await import("@/store/modules/permission")
    usePermissionStoreHook().setFlatteningRoutes(flattenedRoutes)

    console.log("✅ 所有动态路由注册完成")
  } catch (error) {
    console.error("注册路由到Vue Router失败:", error)
    throw error
  }
}

// 获取第一个有权限的页面路径
export const getFirstAvailablePagePath = async (): Promise<string> => {
  console.log("=== 开始获取第一个有权限的页面路径 ===")

  try {
    const { usePermissionStoreHook } = await import("@/store/modules/permission")
    const permissionStore = usePermissionStoreHook()
    const wholeMenus = permissionStore.wholeMenus

    console.log("当前菜单数量:", wholeMenus.length)

    if (!wholeMenus || wholeMenus.length === 0) {
      console.log("没有可用菜单，返回默认工作台路径")
      return "/workbench/dashboard"
    }

    // 递归查找第一个可访问的页面
    const findFirstPage = (menus: any[]): string | null => {
      for (const menu of menus) {
        // 如果是页面类型且有路径，直接返回
        if (menu.path && menu.component && !menu.children?.length) {
          console.log("找到第一个页面:", menu.path, "标题:", menu.meta?.title)
          return menu.path
        }

        // 如果有子菜单，递归查找
        if (menu.children && menu.children.length > 0) {
          const childPath = findFirstPage(menu.children)
          if (childPath) {
            return childPath
          }
        }
      }
      return null
    }

    const firstPagePath = findFirstPage(wholeMenus)

    if (firstPagePath) {
      console.log("找到第一个有权限的页面路径:", firstPagePath)
      return firstPagePath
    } else {
      console.log("没有找到有权限的页面，返回默认工作台路径")
      return "/workbench/dashboard"
    }
  } catch (error) {
    console.error("获取第一个有权限的页面路径失败:", error)
    return "/workbench/dashboard"
  }
}

// 导出权限相关的工具函数
// 注意：避免从此处重导出 API 函数以防循环依赖
export type { PermissionItem }
